import type { AxiosRequestConfig } from 'axios';
import { Parser } from '~/api';
import RequestService from './request';

export interface FileRequest {
    path: string;
}

export type FileContents =
    | ArrayBuffer
    | {
          content: string;
      };

export interface WriteFileRequest extends FileRequest {
    content: string;
    encoding: string;
    bom?: boolean;
}

export interface ManageFilesRequest {
    paths: string[];
}

export interface DownloadFileResponse {
    url: string;
}

export interface UploadConflict {
    path: string;
    type: 'directory' | 'file';
}

export interface RenameFileRequest extends FileRequest {
    to: string;
}

export interface CompressFilesRequest extends ManageFilesRequest {
    to: string;
}

class FilesService {
    getDirectory(req: PaginatableRequest, path: string) {
        return RequestService.get('/servers/:server/files/directory', {
            path,
            ...req,
        }).then(Parser.parse);
    }

    createDirectory(data: FileRequest) {
        return RequestService.post('/servers/:server/files/directory', data);
    }

    getDirectoryUsage(path: string, config?: AxiosRequestConfig): Promise<{ size: number }> {
        return RequestService.get(
            '/servers/:server/files/directory-usage',
            { path },
            {
                // Very forgiving — a recursive `du` on a multi-GB volume can
                // take a while; the daemon serializes the call across the
                // whole node.
                timeout: 600000,
                ...(config ?? {}),
            },
        );
    }

    getUploadConflicts(data: ManageFilesRequest): Promise<UploadConflict[]> {
        return RequestService.post('/servers/:server/files/conflicts', data);
    }

    readFile(data: FileRequest) {
        return RequestService.get('/servers/:server/files/read', data, {
            timeout: 0,
        });
    }

    readBinaryFile(data: FileRequest) {
        return RequestService.get('/servers/:server/files/read', data, {
            headers: { Accept: 'application/octet-stream' },
            responseType: 'arraybuffer',
            timeout: 0,
        });
    }

    writeFile(data: WriteFileRequest) {
        return RequestService.post('/servers/:server/files/write', data);
    }

    copyFile(data: FileRequest) {
        return RequestService.post('/servers/:server/files/copy', data);
    }

    deleteFile(data: ManageFilesRequest) {
        return RequestService.post('/servers/:server/files/delete', data);
    }

    downloadFile(data: FileRequest): Promise<DownloadFileResponse> {
        return RequestService.get('/servers/:server/files/download', data);
    }

    renameFile(data: RenameFileRequest) {
        return RequestService.patch('/servers/:server/files/rename', data);
    }

    compressFile(data: CompressFilesRequest) {
        return RequestService.post('/servers/:server/files/compress', data, {
            timeout: 120000,
        });
    }

    decompressFile(data: FileRequest) {
        return RequestService.post('/servers/:server/files/decompress', data, {
            timeout: 120000,
        });
    }
}

export default new FilesService();
